import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  BarChart3,
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  CloudSun,
  CloudUpload,
  Download,
  Droplet,
  Eye,
  EyeOff,
  Flag,
  Flower2,
  Home,
  Info,
  LayoutDashboard,
  Link as LinkIcon,
  LocateFixed,
  Mail,
  Map,
  MapPinned,
  Menu,
  Minus,
  MoveRight,
  Pencil,
  Plus,
  RefreshCw,
  Satellite,
  ShieldCheck,
  Sprout,
  Table,
  Tractor,
  Trash2,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Undo2,
  Unlink,
  Wind,
  Wrench,
} from "lucide-react";

/**
 * Design-system icon (see the DS ICONOGRAPHY.md).
 *
 * The app's single icon vocabulary. Icons are Lucide, **stroke-only at weight
 * 1.5**, sized on the DS scale (**16 / 20 / 24 only**), and coloured by
 * `currentColor` so they inherit the surrounding text colour.
 *
 * Call sites use `<Icon name="map" />` — they never import from `lucide-react`
 * directly. That keeps stroke/size/colour enforced in one place and lets the
 * whole set be swapped (e.g. for a custom brand set) by editing this file only.
 *
 * Adding an icon: import the Lucide component and add one REGISTRY entry
 * (kebab-case name). Named imports keep tree-shaking — do NOT switch to a
 * dynamic `lucide[name]` lookup, which pulls in the entire library.
 *
 * Sizes: 16 = inline with 13px text · 20 = buttons/nav (default) · 24 = section
 * headers. Colour: omit for currentColor. Pass `color` (a CSS colour string,
 * e.g. a hex) only for data/map legend swatches — the DS carve-out. For a MUI
 * palette colour, set it on the parent (`sx={{ color: "success.main" }}`) and
 * let the icon inherit it via currentColor.
 */
const REGISTRY = {
  "arrow-down": ArrowDown,
  "arrow-left": ArrowLeft,
  "arrow-up": ArrowUp,
  "bar-chart-3": BarChart3,
  "book-open": BookOpen,
  calendar: Calendar,
  check: Check,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "clipboard-list": ClipboardList,
  clock: Clock,
  "cloud-sun": CloudSun,
  "cloud-upload": CloudUpload,
  download: Download,
  droplet: Droplet,
  eye: Eye,
  "eye-off": EyeOff,
  flag: Flag,
  "flower-2": Flower2,
  home: Home,
  info: Info,
  "layout-dashboard": LayoutDashboard,
  link: LinkIcon,
  "locate-fixed": LocateFixed,
  mail: Mail,
  map: Map,
  "map-pinned": MapPinned,
  menu: Menu,
  minus: Minus,
  "move-right": MoveRight,
  pencil: Pencil,
  plus: Plus,
  "refresh-cw": RefreshCw,
  satellite: Satellite,
  "shield-check": ShieldCheck,
  sprout: Sprout,
  table: Table,
  tractor: Tractor,
  "trash-2": Trash2,
  "trending-down": TrendingDown,
  "trending-up": TrendingUp,
  "triangle-alert": TriangleAlert,
  "undo-2": Undo2,
  unlink: Unlink,
  wind: Wind,
  wrench: Wrench,
};

const ALLOWED_SIZES = new Set([16, 20, 24]);

export default function Icon({ name, size = 20, ...props }) {
  const LucideIcon = REGISTRY[name];
  if (!LucideIcon) {
    if (import.meta.env?.DEV) {
      console.warn(`[Icon] unknown name "${name}" — add it to the registry`);
    }
    return null;
  }
  const resolved = ALLOWED_SIZES.has(size) ? size : 20;
  // strokeWidth is fixed at the DS weight; aria-hidden by default (icons here
  // are decorative — labels/aria-labels live on the surrounding control).
  return (
    <LucideIcon size={resolved} strokeWidth={1.5} aria-hidden {...props} />
  );
}
